import { t } from "i18next";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import moment from 'moment';
import { Button, Form } from "react-bootstrap";
import { genderOptions, Modality } from "../../../../interfaces/modality";
import { getModality } from "../../../../services/modality";
import { AdminModalityNew } from "./modality-new";
import { AdminModalityDelete } from "./modality-delete";
import { AdminModalityEdit } from "./modality-edit";
import { getSpecialityOptions } from "../../../../services/speciality";
import { Speciality } from "../../../../interfaces/speciality";
import { tableStyles, getPaginationOptions } from "../../../../util/data-table";

export function AdminModalityList() {
    const [modalities, setModalities] = useState<Modality[]>([]);
    const [specialities, setSpecialities] = useState<Speciality[]>([]);
    const [showAddNew, setShowAddNew] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    
    const CustomDate = (row: any ) => (
        <span>
            { moment(row.row.changed_at).format("DD/MM/YYYY HH:mm") }
        </span>
    );
    
    const CustomActions = (row: any) => (
        <span>
            <a href="#" className="icon-link edit" title={ t("Edit") } onClick={ handleEdit } data-id={ row.row.id }>
                <i className="bi bi-pencil-square"></i>
            </a>
            <a href="#" className="icon-link delete" title={ t("Delete") } onClick={ handleDelete } data-id={ row.row.id }>
                <i className="bi bi-trash"></i>
            </a>
        </span>
    );

    const CustomGender = (row: any) => {
        let label = '';
        const index = genderOptions.findIndex((g: any) => g.value == row.row.gender);
        if (index !== -1) {
            label = t(genderOptions[index].label);
        }
        return (<span>{ label }</span>);
    };

    const columns = [
        {
            id: 'id',
            name: '#',
            selector: (row: any) => row.id,
            sortable: true
        },
        {
            id: 'name',
            name: t("Name"),
            selector: (row: any) => row.name,
            sortable: true
        },
        {
            id: 'speciality_name',
            name: t('speciality'),
            selector: (row: any) => row.speciality_name,
            sortable: true
        },
        {
            id: 'gender',
            name: t('gender'),
            selector: (row: any) => row.gender,
            sortable: true,
            cell: (row: any) => <CustomGender row={row} />,
        },
        {
            id: 'distance',
            name: t('distance'),
            selector: (row: any) => row.distance,
            sortable: true
        },
        {
            id: 'boat',
            name: t('boat'),
            selector: (row: any) => row.boat,
            sortable: true
        },
        {
            id: 'changed_at',
            name: t('changed_at'),
            selector: (row: any) => row.changed_at,
            sortable: true,
            cell: (row: any) => <CustomDate row={row} />,
        },
        {
            id: 'actions',
            name: t("actions"),
            cell: (row: any) => <CustomActions row={row} />,
        }

    ];

    useEffect(() => {
         getSpecialityOptions()
            .then(response => {
                setSpecialities(response.data);
            })
            .catch(error => {
                console.log(error);
            });

        getModality()
            .then(response => {
                setModalities(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const s = event.target.search.value;
        const speciality_id = event.target.speciality_id.value;

        getModality(s, speciality_id)
            .then(response => {
                setModalities(response.data);
            })
            .catch(error => {
                console.log(error);
            });
    }

    const handleAddNew = (event: any) => {
        event.preventDefault();
        setShowAddNew(true)
    }

    const handleAddNewOnSave = (s: Modality) => {
        const newModalities = [
            s,
            ...modalities
        ];
        setModalities(newModalities);
        setShowAddNew(false);
    }

    const handleAddNewOnClose = () => {
        setShowAddNew(false);
    }

    const handleDelete = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowDelete(true);
    }

    const handleDeleteOnDelete = (id: number) => {
        setSelectedId(0);
        const newModalities = modalities.filter((m: Modality) => m.id != id);
        setModalities(newModalities);
        setShowDelete(false);
    }

    const handleDeleteOnClose = () => {
        setSelectedId(0);
        setShowDelete(false);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        setSelectedId(event.currentTarget.dataset.id);
        setShowEdit(true);
    }

    const handleEditOnSave = (changedModality: Modality) => {
        setSelectedId(0);
        const newModalities = modalities.map((m: Modality) => {
            if (m.id == changedModality.id) {
                return changedModality;
            } else {
                return m;
            }
        });
        setModalities(newModalities);
        setShowEdit(false);
    }

    const handleEditOnClose = () => {
        setSelectedId(0);
        setShowEdit(false);
    }

    return (
        <div className="modalities-list-container">
            <h1>
                { t('modalities')}
            </h1>
            <div className="filters-container">
                <Form onSubmit={ handleSearchSubmit }>
                    <Form.Group className="field-search" controlId="search">
                        <Form.Control type="search" placeholder={ t("Write to search") } />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="speciality_id">
                        <Form.Select aria-label={ t("speciality") }>
                            <option value="">{ t("select_speciality") }</option>
                            { specialities.map((s: Speciality) => {
                                return <option key={ 'speciality-' + s.id } value={ s.id }>{ s.name }</option>;
                            })}
                        </Form.Select>
                    </Form.Group>
                    <div className="actions">
                        <Button variant="primary" type="submit">
                            { t("search") }
                        </Button>
                    </div>
                </Form>
            </div>
            <div className="table-container">
                <DataTable
                    columns={ columns }
                    data={modalities}
                    // selectableRows={ true }
                    // selectableRowsHighlight={ true }
                    responsive={ true }
                    striped={ true }
                    highlightOnHover={ true }
                    defaultSortFieldId={ 'changed_at' }
                    defaultSortAsc={ false }
                    pagination={ true }
                    customStyles={ tableStyles }
                    paginationComponentOptions={ getPaginationOptions() }
                />
            </div>            
            <a className="admin-add-new" href="#" onClick={ handleAddNew }>
                <i className="bi bi-plus-circle"></i>
            </a>
            { showAddNew &&
                <AdminModalityNew onSave={ handleAddNewOnSave } onClose={ handleAddNewOnClose } />
            }
            { showDelete && (selectedId != 0) &&
                <AdminModalityDelete id={ selectedId } onDelete={ handleDeleteOnDelete } onClose={ handleDeleteOnClose } />
            }
            { showEdit && (selectedId != 0) &&
                <AdminModalityEdit id={ selectedId } onSave={ handleEditOnSave } onClose={ handleEditOnClose } />
            }
        </div>

    )
}
